import { useId, type CSSProperties } from "react";
import type { FinanceExplorerValues } from "@chro/shared";
import { ArrowDownRight, ArrowRight, ArrowUpRight, CircleAlert } from "lucide-react";
import "./finance-overview.css";

type OverviewValues = Pick<FinanceExplorerValues,
  "recognizedPnl" | "openingCarry" | "closingCarry" | "currentDeferred" |
  "releasedCarry" | "transfers" | "carryRounding">;

export interface FinanceMonthOverviewProps {
  currency: string;
  values: OverviewValues;
  scopeLabel: string;
  month: string;
  reviewCount?: number;
}

type BridgeStep = {
  key: string;
  label: string;
  shortLabel: string;
  value: number | null;
  operation: "start" | "add" | "subtract" | "end";
  note: string;
};

const amountFormat = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthFormat = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
const amount = (value: number | null) => value == null ? "—" : amountFormat.format(value === 0 ? 0 : value);
const monthLabel = (month: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(month)
  ? monthFormat.format(new Date(`${month}-01T00:00:00Z`)) : month;

function bridgeSteps(values: OverviewValues): BridgeStep[] {
  const steps: BridgeStep[] = [
    { key: "opening", label: "Opening carry", shortLabel: "Opening", value: values.openingCarry,
      operation: "start", note: "Balance brought into the month." },
    { key: "deferred", label: "Current movement deferred", shortLabel: "Deferred", value: values.currentDeferred,
      operation: "add", note: "This month’s movement carried forward under the recognition rules." },
    { key: "released", label: "Earlier carry released", shortLabel: "Released", value: values.releasedCarry,
      operation: "subtract", note: "Earlier carry recognized this month. A negative release increases the closing balance." },
  ];
  // An unknown adjustment stays visible. Only a known zero can be omitted.
  if (values.transfers !== 0) steps.push({ key: "transfers", label: "Carry transfers", shortLabel: "Transfers",
    value: values.transfers, operation: "add", note: "Carry moving into or out of this selection." });
  if (values.carryRounding !== 0) steps.push({ key: "rounding", label: "Carry rounding", shortLabel: "Rounding",
    value: values.carryRounding, operation: "add", note: "The report’s existing adjustment for displayed cents." });
  steps.push({ key: "closing", label: "Closing carry", shortLabel: "Closing", value: values.closingCarry,
    operation: "end", note: "Balance carried into the next month." });
  return steps;
}

function CarryWaterfall({ steps, currency }: { steps: BridgeStep[]; currency: string }) {
  const titleId = useId();
  const descriptionId = useId();
  if (steps.some(step => step.value == null)) return <div className="fin-bridge-unavailable">
    <CircleAlert size={20} aria-hidden="true" />
    <div><strong>Carry bridge incomplete</strong><span>The available amounts remain below. Missing values are shown as —.</span></div>
  </div>;

  // These running coordinates position bars only. All financial labels and the
  // closing balance come directly from the report's existing displayed values.
  let position = 0;
  const bars = steps.map(step => {
    const value = step.value!;
    const start = step.operation === "start" || step.operation === "end" ? 0 : position;
    const end = step.operation === "subtract" ? position - value
      : step.operation === "add" ? position + value : value;
    position = end;
    return { step, start, end };
  });
  const extremes = bars.flatMap(bar => [bar.start, bar.end]);
  const minimum = Math.min(0, ...extremes);
  const maximum = Math.max(0, ...extremes);
  const range = maximum - minimum || 1;
  const width = 960;
  const height = 164;
  const top = 16;
  const bottom = 140;
  const column = width / bars.length;
  const barWidth = Math.min(96, column * 0.52);
  const y = (value: number) => bottom - ((value - minimum) / range) * (bottom - top);
  const zero = y(0);
  return <div className="fin-bridge-plot">
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby={titleId} aria-describedby={descriptionId}>
      <title id={titleId}>{`Carry bridge in ${currency}`}</title>
      <desc id={descriptionId}>Opening carry, plus current movement deferred, less earlier carry released,
        with any transfers and rounding, leads to closing carry. Bars show signed movements in carry,
        not profit or loss. Exact report amounts appear below.</desc>
      <line x1="8" x2={width - 8} y1={zero} y2={zero} className="fin-bridge-zero" />
      {bars.map(({ step, start, end }, index) => {
        const x = column * index + (column - barWidth) / 2;
        const isBalance = step.operation === "start" || step.operation === "end";
        const movement = end - start;
        const tone = isBalance ? "balance" : movement > 0 ? "increase" : movement < 0 ? "decrease" : "neutral";
        return <g key={step.key}>
          {index < bars.length - 1 && <line x1={x + barWidth} x2={x + column}
            y1={y(end)} y2={y(end)} className="fin-bridge-connector" />}
          {movement === 0 ? <line x1={x} x2={x + barWidth} y1={y(end)} y2={y(end)} className="fin-bridge-flat" />
            : <rect x={x} y={Math.min(y(start), y(end))} width={barWidth}
              height={Math.max(1, Math.abs(y(end) - y(start)))} rx="3"
              className={`fin-bridge-bar fin-bridge-bar-${tone}`} />}
          <title>{`${step.label}: ${amount(step.value)} ${currency}. ${step.note}`}</title>
          <text x={column * (index + 0.5)} y={height - 3} textAnchor="middle" className="fin-bridge-axis-label">{step.shortLabel}</text>
        </g>;
      })}
    </svg>
  </div>;
}

/** Presentation of already projected Finance amounts; no recognition or rounding policy lives here. */
export function FinanceMonthOverview({ currency, values, scopeLabel, month, reviewCount }: FinanceMonthOverviewProps) {
  const headingId = useId();
  const bridgeId = useId();
  const steps = bridgeSteps(values);
  const pnl = values.recognizedPnl;
  const ResultIcon = pnl == null || pnl === 0 ? ArrowRight : pnl < 0 ? ArrowDownRight : ArrowUpRight;
  const resultLabel = pnl == null ? "Recognition incomplete" : pnl < 0 ? "Recognized loss" : pnl > 0 ? "Recognized profit" : "No recognized result";
  return <section className="fin-overview" aria-label={`Financial overview ${currency}`}>
    <header className="fin-overview-header">
      <div><div className="fin-overview-eyebrow">Monthly perspective <span aria-hidden="true">/</span> {currency}</div>
        <h3 id={headingId}>{monthLabel(month)} at a glance</h3>
        <p className="fin-overview-scope">{scopeLabel}</p>
      </div>
      {reviewCount != null && reviewCount > 0 && <span className="fin-overview-review"><CircleAlert size={14} aria-hidden="true" />
        {reviewCount} {reviewCount === 1 ? "check" : "checks"} to review</span>}
    </header>

    <div className="fin-kpis">
      <article className="fin-kpi fin-kpi-primary">
        <div className="fin-kpi-top"><h4 className="fin-label">Recognized P&amp;L</h4><span className="fin-kpi-currency">{currency}</span></div>
        <p className="fin-value">{amount(pnl)}</p>
        <div className={`fin-kpi-caption ${pnl != null && pnl < 0 ? "fin-kpi-caption-loss" : ""}`}><ResultIcon size={17} aria-hidden="true" />{resultLabel}</div>
        <p className="fin-kpi-note">Recognized under CASCENA’s rules for this month.</p>
      </article>
      <article className="fin-kpi fin-kpi-opening">
        <div className="fin-kpi-top"><h4 className="fin-label">Opening carry</h4><span className="fin-kpi-index" aria-hidden="true">01</span></div>
        <p className="fin-value">{amount(values.openingCarry)} <span className="fin-value-unit">{currency}</span></p>
        <p className="fin-kpi-note">Brought into the month</p>
        <div className="fin-kpi-rule" aria-hidden="true" />
        <p className="fin-kpi-definition">The carry balance at the start of the month.</p>
      </article>
      <article className="fin-kpi fin-kpi-closing">
        <div className="fin-kpi-top"><h4 className="fin-label">Closing carry</h4><span className="fin-kpi-index" aria-hidden="true">02</span></div>
        <p className="fin-value">{amount(values.closingCarry)} <span className="fin-value-unit">{currency}</span></p>
        <p className="fin-kpi-note">Carried into the next month</p>
        <div className="fin-kpi-rule" aria-hidden="true" />
        <p className="fin-kpi-definition">The closing balance under the recognition rules.</p>
      </article>
    </div>

    <section className="fin-bridge" aria-labelledby={bridgeId}>
      <div className="fin-bridge-header"><div><div className="fin-overview-eyebrow">The carry movement</div>
        <h4 id={bridgeId}>From opening to closing</h4></div>
        <div className="fin-bridge-legend" aria-label="Chart colours"><span><i className="fin-legend-increase" />Carry increases</span><span><i className="fin-legend-decrease" />Carry decreases</span></div>
      </div>
      <CarryWaterfall steps={steps} currency={currency} />
      <ol className="fin-bridge-steps" style={{ "--fin-step-count": steps.length } as CSSProperties}>
        {steps.map(step => <li key={step.key} className={`fin-bridge-step fin-bridge-step-${step.operation}`}>
          <div className="fin-bridge-step-label"><span className="fin-bridge-operator" aria-label={step.operation === "subtract" ? "subtract" : step.operation === "add" ? "add" : step.operation === "end" ? "equals" : "start"}>
            {step.operation === "subtract" ? "−" : step.operation === "add" ? "+" : step.operation === "end" ? "=" : "01"}
          </span><span>{step.label}</span></div>
          <div className="fin-bridge-amount">{amount(step.value)}</div>
          <p className="fin-bridge-step-note">{step.note}</p>
        </li>)}
      </ol>
      <p className="fin-bridge-footnote">Amounts in {currency}. Carry is the balance between accrued economics and recognition; it can be positive or negative.</p>
    </section>
  </section>;
}
